'use client'

import { useState, useEffect, useRef } from 'react'
import { Share2, Copy } from 'lucide-react'
import { Button } from '~/components/ui/button'
import { ShareQR } from '~/components/ui/share-qr'
import { trackActivity } from '~/lib/activity-tracking'
import { facilitatorProfilePath, resolveShareUrl } from '~/lib/share-url'
import { env } from '~/env'

type Props = {
  facilitatorId: string
  accessToken?: string
  /**
   * #1576 — `cream` matches `FacilitatorEditButton`'s rounded-full
   * border-[#D9CDB9]/bg-[#FFFDF8] treatment, for the hero's owner-only
   * layout where this button sits right next to that one (`/hosting/profile`
   * and the admin edit view). Everywhere else (the public profile page, no
   * Edit button) keeps the default coral/salmon — same pattern as
   * `FacilitatorFollowButton`'s `tone` prop.
   */
  tone?: "salmon" | "cream"
}

/**
 * #1118 — the share URL is derived here, from the origin the page is actually
 * being viewed on, and is NOT a prop.
 *
 * It used to be `profileUrl`, passed in by the profile page, which hardcoded
 * the bare `wellmate.me` apex — the old marketing site. Because that one prop
 * feeds every channel below (clipboard, Instagram, WhatsApp, LinkedIn and the
 * displayed value), a single wrong string broke all of them at once.
 *
 * Removing the prop is the fix, not just correcting its value: the profile
 * page is a server component and has no access to the request origin, so
 * anything it could pass would be a build-time or hardcoded guess. Deriving it
 * from `facilitatorId` in this client component leaves no parameter for a
 * future caller to get wrong. `test/no-marketing-site-links.test.ts` fails the
 * build if the literal comes back.
 */
export function ProfileShareButton({ facilitatorId, accessToken, tone = "salmon" }: Props) {
  const [isOpen, setIsOpen] = useState(false)
  const [copied, setCopied] = useState(false)
  // #1305 — a denied clipboard permission (Safari, non-secure context,
  // iframe) used to show "Copied!" regardless, since the write was never
  // awaited/caught. Decided 2026-10-05 (Ed): show this instead.
  const [copyFailed, setCopyFailed] = useState(false)
  const [origin, setOrigin] = useState('')
  const popupRef = useRef<HTMLDivElement | null>(null)

  // Same origin-at-render convention as ShareQR — see lib/share-url.ts for why
  // a build-time env var is not trustworthy here.
  useEffect(() => {
    setOrigin(window.location.origin)
  }, [])

  const profileUrl = resolveShareUrl(
    origin || env.NEXT_PUBLIC_WEB_URL,
    facilitatorProfilePath(facilitatorId),
  )

  const handleCopy = async () => {
    if (!profileUrl) return
    try {
      await navigator.clipboard.writeText(profileUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      setCopyFailed(true)
      setTimeout(() => setCopyFailed(false), 2000)
    }
  }

  // #1563 — the native share sheet where available (mobile Safari/Chrome),
  // the existing popup (clipboard + socials + QR) everywhere else. A user
  // dismissing the native sheet (AbortError) is not a failure — do nothing,
  // same as closing the popup without picking anything.
  const handleShareClick = async () => {
    if (typeof navigator !== 'undefined' && 'share' in navigator && profileUrl) {
      trackActivity('PROFILE_SHARE', 'FACILITATOR', facilitatorId, {
        accessToken,
        skipDedup: true,
      })
      try {
        await navigator.share({ title: 'Path Collective facilitator profile', url: profileUrl })
      } catch (err) {
        if ((err as Error)?.name !== 'AbortError') {
          setIsOpen(true)
        }
      }
      return
    }
    const opening = !isOpen
    setIsOpen(opening)
    if (opening) {
      trackActivity('PROFILE_SHARE', 'FACILITATOR', facilitatorId, {
        accessToken,
        skipDedup: true,
      })
    }
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <div className="relative inline-block" ref={popupRef}>
      <Button
        onClick={() => void handleShareClick()}
        variant="outline"
        size="icon"
        className={
          tone === "cream"
            ? "rounded-full border-[#D9CDB9] bg-[#FFFDF8] text-[#2C2D2B] shadow-none hover:bg-[#F7F0E6]"
            : "border-[#DE866D] text-[#DE866D] hover:bg-[#DE866D] hover:text-white"
        }
        aria-label="Share"
        title="Share"
      >
        <Share2 className="h-5 w-5" />
      </Button>

      {isOpen && (
        <div className="absolute z-50 mt-2 w-64 bg-white border border-gray-200 rounded-xl shadow-lg p-3 right-0">
          <p className="text-sm font-medium text-gray-700 mb-2">Share this profile</p>

          {/* FR-3 — never offer a domain-less or wrong-domain link. In practice
              the origin is resolved before anyone can click the trigger, so
              this branch is a guarantee rather than a state users will see;
              the point is that no code path below can run without a real URL. */}
          {!profileUrl ? (
            <p className="text-xs text-gray-500 py-6 text-center" data-testid="profile-share-pending">
              Preparing link…
            </p>
          ) : (
          <>
          <div className="flex justify-around mb-2">
            {/* #1174 FR-5 — Messenger dropped: the dialog/send link has no
                app_id and never worked (same decision as #1166 FR-6). */}

            {/* Instagram — copy link since IG doesn't support URL sharing */}
            <button
              onClick={(e) => {
                e.stopPropagation()
                void handleCopy()
                window.open('https://www.instagram.com/', '_blank')
              }}
              className="p-2 rounded-full hover:bg-gray-100 transition"
              title="Copy link & open Instagram"
            >
              <img
                src="https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/instagram.svg"
                alt="Instagram"
                className="w-5 h-5"
              />
            </button>

            {/* WhatsApp */}
            <a
              href={`https://wa.me/?text=${encodeURIComponent(profileUrl)}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="p-2 rounded-full hover:bg-gray-100 transition"
              title="Share on WhatsApp"
              data-testid="profile-share-whatsapp"
            >
              <img
                src="https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/whatsapp.svg"
                alt="WhatsApp"
                className="w-5 h-5"
              />
            </a>

            {/* LinkedIn */}
            <a
              href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(profileUrl)}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="p-2 rounded-full hover:bg-gray-100 transition"
              title="Share on LinkedIn"
              data-testid="profile-share-linkedin"
            >
              <img
                src="https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/linkedin.svg"
                alt="LinkedIn"
                className="w-5 h-5"
              />
            </a>
          </div>

          <div className="flex items-center border border-gray-200 rounded-lg px-2 py-1">
            <input
              readOnly
              value={profileUrl}
              data-testid="profile-share-url"
              aria-label="Profile link"
              className="text-xs text-gray-500 flex-grow bg-transparent outline-none truncate"
            />
            <button
              onClick={(e) => { e.stopPropagation(); void handleCopy() }}
              className="p-1 hover:bg-gray-100 rounded-md transition flex-shrink-0"
              title="Copy Link"
            >
              <Copy className="h-4 w-4 text-gray-600" />
            </button>
          </div>

          {copied && (
            <p className="text-green-600 text-xs mt-1 text-right">Copied!</p>
          )}
          {copyFailed && (
            <p className="text-red-600 text-xs mt-1 text-right">
              Couldn&apos;t copy — select the link above
            </p>
          )}

          {/* #1019 decision 2 — the footer QR block became the CTA banner, so
              in-person sharing lives here instead. Same affordance, one fewer
              control in the footer row that caused #979's 360px overflow. */}
          <div className="mt-3 border-t border-gray-200 pt-3 flex flex-col items-center gap-1.5">
            <ShareQR path={facilitatorProfilePath(facilitatorId)} size={104} caption={null} />
            <p className="text-[11px] text-gray-500">Scan to open on a phone</p>
          </div>
          </>
          )}
        </div>
      )}
    </div>
  )
}
